import React from "react";
import { Form, Input } from "antd";
import { ColumnType } from "antd/lib/table";
import { CheckboxOptionType } from "antd/es/checkbox";
import { compact, lowerCase } from "lodash";
import { DropdownInput } from "components";
import { EMPTY } from "constants/common";
import { AiccLessonEntry, AiccLessonEntryPayload } from "types/model/aiccLearningAssets";
import getActionColumn from "../getActionColumn";
import styles from "../LearningAssets.module.scss";

const renderPreviewCell = (value: string | number) => value || EMPTY;

export const getPreviewColumns = (textLabel: string, hasTransliteration?: boolean) =>
  compact<ColumnType<AiccLessonEntryPayload>>([
    { title: "Level", dataIndex: "level", key: "level", width: 80, render: renderPreviewCell },
    { title: "Lesson", dataIndex: "lesson", key: "lesson", width: 80, render: renderPreviewCell },
    { title: textLabel, dataIndex: "text", key: "text", width: 240, render: renderPreviewCell },
    { title: "Translation", dataIndex: "translation", key: "translation", width: 240, render: renderPreviewCell },
    hasTransliteration && {
      title: "Transliteration",
      dataIndex: "transliteration",
      key: "transliteration",
      width: 200,
      render: renderPreviewCell,
    },
  ]);

interface ColumnHandlers {
  textLabel: string;
  hasTransliteration: boolean;
  canManage: boolean;
  canDelete: boolean;
  isActionDisabled: boolean;
  isSaving: boolean;
  editingId?: string;
  levelOptions: CheckboxOptionType[];
  getLessonOptions: (level?: number) => CheckboxOptionType[];
  onEdit: (entry: AiccLessonEntry) => void;
  onDelete: (entry: AiccLessonEntry) => void;
  onSave: () => void;
  onCancelEdit: () => void;
}

const getColumns = ({
  textLabel,
  hasTransliteration,
  canManage,
  canDelete,
  isActionDisabled,
  isSaving,
  editingId,
  levelOptions,
  getLessonOptions,
  onEdit,
  onDelete,
  onSave,
  onCancelEdit,
}: ColumnHandlers) => {
  const isEditing = (entry: AiccLessonEntry) => entry.id === editingId;

  const renderTextCell = (name: "text" | "translation" | "transliteration", label: string, required: boolean) => (
    value: string,
    entry: AiccLessonEntry
  ) =>
    isEditing(entry) ? (
      <Form.Item
        name={name}
        className={styles.tableCellFormItem}
        rules={required ? [{ required: true, whitespace: true, message: `Please enter a ${lowerCase(label)}.` }] : []}
      >
        <Input placeholder={label} />
      </Form.Item>
    ) : (
      value || EMPTY
    );

  return compact<ColumnType<AiccLessonEntry>>([
    {
      title: "Level",
      dataIndex: "level",
      key: "level",
      width: 140,
      render: (level, entry) =>
        isEditing(entry) ? (
          <Form.Item
            name="level"
            className={styles.tableCellFormItem}
            rules={[{ required: true, message: "Please select a level." }]}
          >
            <DropdownInput options={levelOptions} placeholder="Level" mode="single" />
          </Form.Item>
        ) : (
          level ?? EMPTY
        ),
    },
    {
      title: "Lesson",
      dataIndex: "lesson",
      key: "lesson",
      width: 140,
      render: (lesson, entry) =>
        isEditing(entry) ? (
          <Form.Item noStyle shouldUpdate={(prev, next) => prev.level !== next.level}>
            {({ getFieldValue }) => (
              <Form.Item
                name="lesson"
                className={styles.tableCellFormItem}
                rules={[{ required: true, message: "Please select a lesson." }]}
              >
                <DropdownInput options={getLessonOptions(getFieldValue("level"))} placeholder="Lesson" mode="single" />
              </Form.Item>
            )}
          </Form.Item>
        ) : (
          lesson ?? EMPTY
        ),
    },
    {
      title: textLabel,
      dataIndex: "text",
      key: "text",
      width: 280,
      render: renderTextCell("text", textLabel, true),
    },
    {
      title: "Translation",
      dataIndex: "translation",
      key: "translation",
      width: 280,
      render: renderTextCell("translation", "Translation", true),
    },
    hasTransliteration && {
      title: "Transliteration",
      dataIndex: "transliteration",
      key: "transliteration",
      width: 240,
      render: renderTextCell("transliteration", "Transliteration", false),
    },
    canManage &&
      getActionColumn<AiccLessonEntry>({
        canDelete,
        isDisabled: isActionDisabled,
        isSaving,
        isEditing,
        onEdit,
        onDelete,
        onSave,
        onCancelEdit,
      }),
  ]);
};

export default getColumns;
